import { useState, useEffect, useRef } from 'react';
import { Search, Star } from 'lucide-react';
import { ChartCaptureButton } from './ChartCapture';
import SearchSuggestions from './SearchSuggestions';
import WatchlistModal from './WatchlistModal';
import {
    Typography, TextField, Tooltip
} from '@mui/material';


const ChartHeader = ({
    searchInput,
    onSearchChange,
    onSearchSubmit,
    currentSymbol,
    stockInfo,
    isCapturing,
    onCapture,
    stockDataMap,
    allData
}) => {
    const [isSearchExpanded, setIsSearchExpanded] = useState(true);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showWatchlist, setShowWatchlist] = useState(false);
    const [isInWatchlist, setIsInWatchlist] = useState(false);
    const searchRef = useRef(null);
    const watchlistButtonRef = useRef(null);

    const handleWatchlistChange = () => {
        if (!currentSymbol) return;

        const saved = localStorage.getItem('stockWatchlist');
        const list = saved ? JSON.parse(saved) : [];
        setIsInWatchlist(list.includes(currentSymbol));
    };

    const toggleWatchlist = () => {
        if (!currentSymbol) return;

        const saved = localStorage.getItem('stockWatchlist');
        let list = saved ? JSON.parse(saved) : [];

        if (list.includes(currentSymbol)) {
            list = list.filter(s => s !== currentSymbol);
        } else {
            list.push(currentSymbol);
        }

        localStorage.setItem('stockWatchlist', JSON.stringify(list));
        setIsInWatchlist(list.includes(currentSymbol));
    };

    const handleStarClick = (e) => {
        e.stopPropagation();
        setShowWatchlist(true);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!currentSymbol) {
            setIsInWatchlist(false);
            return;
        }

        const saved = localStorage.getItem('stockWatchlist');
        if (saved) {
            const watchlist = JSON.parse(saved);
            setIsInWatchlist(watchlist.includes(currentSymbol));
        }
    }, [currentSymbol]);

    useEffect(() => {
        if (!searchInput.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            setSelectedIndex(-1);
            return;
        }

        // ⭐ ลบการเช็ค allData ออก - ให้แสดง suggestions จาก stockDataMap ได้เลย
        const searchTerm = searchInput.toUpperCase().trim();

        // ⭐ สร้าง Set ของ symbols ที่มีข้อมูล (ถ้ามี)
        const availableSymbols = new Set();
        if (Array.isArray(allData) && allData.length > 0) {
            allData.forEach(item => {
                if (item && typeof item.symbol === 'string' && item.symbol.trim()) {
                    availableSymbols.add(item.symbol.toUpperCase());
                }
            });
        }

        let filtered = [];

        // ⭐ แสดง suggestions จาก stockDataMap ทั้งหมด (ไม่จำกัดว่าต้องมีใน allData)
        if (stockDataMap && stockDataMap.size > 0) {
            Array.from(stockDataMap.entries()).forEach(([symbol, info]) => {
                const matchSymbol = symbol.toUpperCase().includes(searchTerm);
                const matchDesc = info.description?.toLowerCase().includes(searchInput.toLowerCase());

                if (matchSymbol || matchDesc) {
                    filtered.push({
                        symbol: symbol.toUpperCase(),
                        name: info.name || symbol,
                        description: info.description || `${symbol} (No description)`,
                        exchange: info.exchange || 'Unknown',
                        logo_url: info.logo_url || null,
                        hasData: availableSymbols.has(symbol.toUpperCase()) // ⭐ เช็คว่ามีข้อมูลหรือไม่
                    });
                }
            });
        }

        // ⭐ ถ้ามี allData เพิ่ม symbols ที่ไม่มีใน stockDataMap
        if (availableSymbols.size > 0) {
            availableSymbols.forEach(symbol => {
                if (!symbol.includes(searchTerm)) return;

                const exists = filtered.some(item => item.symbol === symbol);

                if (!exists) {
                    filtered.push({
                        symbol: symbol,
                        name: symbol,
                        description: `${symbol} (No company info)`,
                        exchange: 'Unknown',
                        logo_url: null,
                        hasData: true
                    });
                }
            });
        }

        filtered.sort((a, b) => {
            const aStarts = a.symbol.startsWith(searchTerm);
            const bStarts = b.symbol.startsWith(searchTerm);

            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;

            return a.symbol.localeCompare(b.symbol);
        });

        filtered = filtered.slice(0, 8);

        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
        setSelectedIndex(-1);
    }, [searchInput, stockDataMap, allData]);

    const handleSearchClick = () => {
        if (currentSymbol) {
            setIsSearchExpanded(true);
            onSearchChange({ target: { value: '' } });
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            const selectedStock = suggestions[selectedIndex];

            onSearchChange({ target: { value: selectedStock.symbol } });

            const fakeEvent = {
                preventDefault: () => { },
                target: { value: selectedStock.symbol }
            };
            onSearchSubmit(fakeEvent);

            setIsSearchExpanded(false);
            setShowSuggestions(false);
            setSelectedIndex(-1);
        } else {
            onSearchSubmit(e || { preventDefault: () => { } });
            setIsSearchExpanded(false);
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                handleSubmit(e);
                break;
            case 'Escape':
                e.preventDefault();
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSuggestionClick = (symbol) => {
        onSearchChange({ target: { value: symbol } });

        const fakeEvent = {
            preventDefault: () => { },
            target: { value: symbol }
        };

        onSearchSubmit(fakeEvent);

        setIsSearchExpanded(false);
        setShowSuggestions(false);
        setSelectedIndex(-1);
    };

    const handleWatchlistSelect = (symbol) => {
        onSearchChange({ target: { value: symbol } });

        const fakeEvent = {
            preventDefault: () => { },
            target: { value: symbol }
        };

        onSearchSubmit(fakeEvent);
    };

    const shouldShowCollapsed = currentSymbol && !isSearchExpanded;

    return (
        <div className="chart-header-container flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2 ml-3" ref={searchRef}>
                {shouldShowCollapsed ? (
                    <button
                        key="collapsed"
                        onClick={handleSearchClick}
                        className="flex items-center gap-1.5 bg-dark-secondary py-1 px-2 transition-colors search-box-collapsed overflow-hidden rounded-none hover:bg-transparent"
                    >
                        {isSearchExpanded && <Search className="icon-sm text-gray-400 flex-shrink-0" />}
                        <span className="text-white font-semibold text-lg whitespace-nowrap">
                            {/* {currentSymbol} */}
                            <Typography variant="h5" gutterBottom>
                                {currentSymbol}
                            </Typography>
                        </span>
                    </button>
                ) : (
                    <div className="relative" style={{ width: '400px' }}>
                        <div
                            key="expanded"
                            className="search-box-expanded rounded-3xl"
                        >
                            <div className="relative h-full">
                                <Search className="absolute left-3 top-1/2 transform translate-y-neg-50 icon-sm text-gray-400 z-10" />
                                {/* <input
                                    type="text"
                                    placeholder="Search symbol or company..."
                                    value={searchInput}
                                    onChange={onSearchChange}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => searchInput && setShowSuggestions(true)}
                                    onBlur={() => {
                                        if (currentSymbol && !searchInput) {
                                            setTimeout(() => setIsSearchExpanded(false), 200);
                                        }
                                    }}
                                    className="input-search-expanded bg-transparent text-white placeholder-gray-500 rounded-3xl px-1 py-2 pl-10 pr-3 text-sm focus-outline-none focus-ring-3 w-full h-full"
                                    autoFocus={isSearchExpanded}
                                /> */}
                                <TextField
                                    type="text"
                                    placeholder="Search symbol or company..."
                                    value={searchInput}
                                    onChange={onSearchChange}
                                    onKeyDown={handleKeyDown}
                                    autoFocus={isSearchExpanded}
                                    onFocus={() => searchInput && setShowSuggestions(true)}
                                    onBlur={() => {
                                        if (currentSymbol && !searchInput) {
                                            setTimeout(() => setIsSearchExpanded(false), 200);
                                        }
                                    }}
                                    autoComplete='off'
                                    variant="outlined"
                                    size='small'
                                    InputProps={{
                                        sx: {
                                            bgcolor: 'transparent',
                                            color: 'white',
                                            borderRadius: '24px',
                                            fontSize: {
                                                xs: '0.75rem',
                                                sm: '0.875rem',
                                                md: '1rem',
                                            },
                                        },
                                    }}
                                    inputProps={{
                                        sx: {
                                            paddingLeft: '2.5rem',
                                            paddingRight: '0.75rem',
                                            '&::placeholder': {
                                                color: '#9CA3AF',
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        {showSuggestions && (
                            <SearchSuggestions
                                suggestions={suggestions}
                                selectedIndex={selectedIndex}
                                onSuggestionClick={handleSuggestionClick}
                                stockDataMap={stockDataMap}
                            />
                        )}
                    </div>
                )}
                {shouldShowCollapsed && (
                    <div className="flex items-center gap-2 animate-slideIn">
                        <div className="h-6 w-px bg-gray-600"></div>
                        <span className="text-white text-xl whitespace-nowrap overflow-hidden text-ellipsis max-w-280px">
                            {/* {stockInfo?.description || 'No company info'} */}
                            <Tooltip
                                title={stockInfo?.description || 'No company info'}
                                placement="bottom"
                                arrow
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            fontSize: '0.875rem',
                                            maxWidth: 300,
                                            whiteSpace: 'normal',
                                        },
                                    },
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{
                                        maxWidth: {
                                            xs: 100,
                                            sm: 100,
                                            md: 150,
                                        },
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        cursor: 'default',
                                    }}
                                >
                                    {stockInfo?.description || 'No company info'}
                                </Typography>
                            </Tooltip>
                        </span>
                        <div className="h-6 w-px bg-gray-600"></div>
                        <span className="text-gray-400 text-xl whitespace-nowrap">
                            {/* {stockInfo?.exchange || 'Unknown'} */}
                            <Typography variant="subtitle2" gutterBottom>
                                {stockInfo?.exchange || 'Unknown'}
                            </Typography>
                        </span>

                        <button
                            ref={watchlistButtonRef}
                            onClick={handleStarClick}
                            className="ml-3 p-1.5 hover-bg-gray-700 rounded-full transition-colors"
                            title="Open watchlist"
                        >
                            <Star className={`icon-md transition-colors ${isInWatchlist
                                ? 'fill-fafafa text-fafafa'
                                : 'text-gray-400'
                                }`} />
                        </button>
                    </div>
                )}
            </div>
            <div className="absolute left-1/2 transform translate-x-neg-50">
                {/* <span className="header-title text-26px font-bold text-white tracking-wide">
                    FLOW US
                </span> */}
                <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: '1vh' }}>
                    FLOW US
                </Typography>
            </div>

            <div className="flex items-center">
                <div className="legend-container flex items-center pr-7 gap-3">
                    <div className="flex items-center gap-2">
                        <div className="legend-line-close w-12 h-0.5" style={{ backgroundColor: '#ff6b35' }}></div>
                        <span className="text-white text-sm">Close</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="legend-line-flow w-12 h-0.5" style={{ backgroundColor: '#00d9a5' }}></div>
                        <span className="text-white text-sm">Flow</span>
                    </div>
                </div>

                <ChartCaptureButton
                    onCapture={onCapture}
                    isCapturing={isCapturing}
                />
            </div>

            <WatchlistModal
                isOpen={showWatchlist}
                onClose={() => setShowWatchlist(false)}
                onSelectSymbol={handleWatchlistSelect}
                stockDataMap={stockDataMap}
                allData={allData}
                buttonRef={watchlistButtonRef}
                onWatchlistChange={handleWatchlistChange}
                currentSymbol={currentSymbol}
            />
        </div>
    );
};

export default ChartHeader;